import { useState } from "react";

const topPicks = [
  {
    id: "natural-linen",
    name: "Natural Linen Sheer Curtains",
    description: "A classic natural linen panel that filters light beautifully and drapes with effortless weight.",
    priceRange: "Mid-range",
    buttonLabel: "View Curtain Options",
    icon: "ri-layout-column-line",
    tag: "Editor's Pick",
    affiliateUrl: "https://amzn.to/4tz8yPK",
  },
  {
    id: "warm-white",
    name: "Warm White Linen Curtains",
    description: "A warm white linen panel that brightens the room without the harshness of stark white.",
    priceRange: "Budget to Mid-range",
    buttonLabel: "See White Linen Options",
    icon: "ri-layout-column-line",
    tag: "Most Popular",
    affiliateUrl: "https://amzn.to/4tz8yPK",
  },
  {
    id: "oat-linen",
    name: "Oat Linen Blackout Curtains",
    description: "A warm oat tone with a thermal lining — soft look with full light control.",
    priceRange: "Mid-range",
    buttonLabel: "Explore Oat Curtains",
    icon: "ri-layout-column-line",
    tag: "Best for Sleep",
    affiliateUrl: "https://amzn.to/4tz8yPK",
  },
];

const budgetCurtains = [
  {
    id: "budget-1",
    name: "Affordable Linen-Look Curtains",
    description: "A budget-friendly option that captures the linen aesthetic without the full price tag.",
    priceRange: "Under $60 per panel",
    buttonLabel: "View Budget Options",
    icon: "ri-layout-column-line",
    tag: "Best Value",
    affiliateUrl: "",
  },
  {
    id: "budget-2",
    name: "Simple Ivory Rod Pocket Curtains",
    description: "Clean, minimal, and warm — a great starting point for a calm neutral room.",
    priceRange: "Under $80 per panel",
    buttonLabel: "See Affordable Options",
    icon: "ri-layout-column-line",
    tag: "Smart Pick",
    affiliateUrl: "",
  },
];

const midRangeCurtains = [
  {
    id: "mid-1",
    name: "Quality Linen Pinch Pleat Curtains",
    description: "A tailored pinch pleat header in natural linen — elevated and timeless.",
    priceRange: "$100–$200 per panel",
    buttonLabel: "View Linen Options",
    icon: "ri-layout-column-line",
    tag: "Most Elegant",
    affiliateUrl: "",
  },
  {
    id: "mid-2",
    name: "Linen-Cotton Blend Eyelet Curtains",
    description: "A relaxed eyelet header in a warm linen-cotton blend — easy to hang and beautifully soft.",
    priceRange: "$80–$160 per panel",
    buttonLabel: "Explore Mid-Range",
    icon: "ri-layout-column-line",
    tag: "Most Relaxed",
    affiliateUrl: "",
  },
];

const splurgeCurtains = [
  {
    id: "splurge-1",
    name: "Belgian Linen Curtains",
    description: "Premium Belgian linen with exceptional drape and a warm natural tone — the gold standard.",
    priceRange: "$250+ per panel",
    buttonLabel: "View Luxury Curtains",
    icon: "ri-layout-column-line",
    tag: "Heirloom Quality",
    affiliateUrl: "",
  },
  {
    id: "splurge-2",
    name: "Custom Linen Drapes",
    description: "Made-to-measure linen drapes in your exact length and tone — the most considered option.",
    priceRange: "$300+ per panel",
    buttonLabel: "Explore Custom Options",
    icon: "ri-layout-column-line",
    tag: "Bespoke",
    affiliateUrl: "",
  },
];

const pairWith = [
  {
    id: "sofa",
    name: "Beige Linen Sofa",
    description: "A warm beige sofa that echoes the linen curtains and anchors the room.",
    priceRange: "Mid-range",
    buttonLabel: "View Sofa Options",
    icon: "ri-sofa-line",
    tag: "Perfect Pair",
    affiliateUrl: "https://amzn.to/4sd5Qyh",
  },
  {
    id: "rug",
    name: "Neutral Area Rug",
    description: "A soft textured rug that grounds the room and completes the warm neutral palette.",
    priceRange: "Budget to Mid-range",
    buttonLabel: "View Rug Options",
    icon: "ri-layout-grid-line",
    tag: "Essential Layer",
    affiliateUrl: "https://amzn.to/4dx4Ykf",
  },
  {
    id: "table",
    name: "Light Wood Coffee Table",
    description: "A warm wood table that adds natural contrast and structure to the soft room.",
    priceRange: "Mid-range",
    buttonLabel: "Explore Coffee Tables",
    icon: "ri-table-line",
    tag: "Finishing Touch",
    affiliateUrl: "https://amzn.to/4tANU1D",
  },
  {
    id: "rod",
    name: "Warm Metal Curtain Rod",
    description: "A brushed brass or warm bronze rod that adds a subtle metallic accent without competing.",
    priceRange: "Budget to Mid-range",
    buttonLabel: "View Rod Options",
    icon: "ri-subtract-line",
    tag: "Hardware",
    affiliateUrl: "",
  },
];

function ProductCard({
  name, description, priceRange, buttonLabel, icon, tag, affiliateUrl,
}: {
  name: string; description: string; priceRange: string; buttonLabel: string;
  icon: string; tag: string; affiliateUrl: string;
}) {
  const [hovered, setHovered] = useState(false);
  const hasLink = affiliateUrl.trim().length > 0;

  return (
    <div
      className="group border border-[#EDE6DA] rounded-xl p-6 bg-[#FDFAF5] hover:border-[#C8BAA8] transition-all duration-300 flex flex-col gap-4"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#F5F1E8]">
          <i className={`${icon} text-[#8B8378] text-base`} />
        </div>
        <span className="text-[#B5A898] text-[9px] tracking-[0.35em] uppercase border border-[#EDE6DA] px-2.5 py-1 rounded-full" style={{ fontFamily: "'Inter', sans-serif" }}>{tag}</span>
      </div>
      <h3 className="text-[#2C2825] text-lg leading-snug" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}>{name}</h3>
      <p className="text-[#8B8378] text-sm leading-relaxed flex-1" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>{description}</p>
      <div className="flex items-center gap-2">
        <span className="text-[#B5A898] text-[10px] tracking-[0.3em] uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>Price</span>
        <span className="w-4 h-px bg-[#EDE6DA]" />
        <span className="text-[#6B5E53] text-xs" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}>{priceRange}</span>
      </div>
      {hasLink ? (
        <a
          href={affiliateUrl}
          target="_blank"
          rel="nofollow noopener noreferrer"
          className="mt-1 self-start inline-flex items-center gap-2 border border-[#C8BAA8] text-[#6B5E53] px-5 py-2.5 rounded-full text-xs tracking-[0.14em] hover:bg-[#2C2825] hover:text-white hover:border-[#2C2825] transition-all duration-300 cursor-pointer whitespace-nowrap"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {buttonLabel}
          <span className="w-3 h-3 flex items-center justify-center">
            <i className={`ri-arrow-right-line text-[10px] transition-transform duration-200 ${hovered ? "translate-x-0.5" : ""}`} />
          </span>
        </a>
      ) : (
        <div
          className="mt-1 self-start inline-flex items-center gap-2 border border-[#EDE6DA] text-[#C8BAA8] px-5 py-2.5 rounded-full text-xs tracking-[0.14em] whitespace-nowrap select-none"
          style={{ fontFamily: "'Inter', sans-serif" }}
          title="Link coming soon"
        >
          {buttonLabel}
          <span className="w-3 h-3 flex items-center justify-center"><i className="ri-time-line text-[10px]" /></span>
        </div>
      )}
    </div>
  );
}

function SectionDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-5 mb-10">
      <div className="flex-1 h-px bg-[#EDE6DA]" />
      <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase flex-shrink-0" style={{ fontFamily: "'Inter', sans-serif" }}>{label}</p>
      <div className="flex-1 h-px bg-[#EDE6DA]" />
    </div>
  );
}

function ProductGrid({ products, cols = 2 }: { products: typeof topPicks; cols?: 2 | 3 }) {
  return (
    <div className={`grid grid-cols-1 ${cols === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"} gap-5`}>
      {products.map((p) => <ProductCard key={p.id} {...p} />)}
    </div>
  );
}

export default function LCShopSection() {
  return (
    <section className="max-w-2xl mx-auto px-4 md:px-0 py-4 mb-16">

      <div id="top-picks" className="mb-16">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>Curtain Edit</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>Our Top Linen Curtain Picks</h2>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-2" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          These are the linen curtains we keep recommending — chosen for their warm tone, natural drape, and ability to transform a room without overpowering it.
        </p>
        <div className="w-12 h-px bg-[#C8BAA8] mb-10" />
        <ProductGrid products={topPicks} cols={3} />
      </div>

      <SectionDivider label="By Budget" />

      <div id="budget-curtains" className="mb-16">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>Budget</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>Best Linen Curtains Under $80 per Panel</h2>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-2" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          A calm, well-styled window treatment does not have to be expensive. These picks keep the tone warm and the look considered.
        </p>
        <div className="w-12 h-px bg-[#C8BAA8] mb-10" />
        <ProductGrid products={budgetCurtains} cols={2} />
      </div>

      <div id="mid-range-curtains" className="mb-16">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>Mid-Range</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>Elevated Linen Curtains $80–$200 per Panel</h2>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-2" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          In this range, fabric quality and construction improve noticeably. These curtains drape beautifully and feel genuinely refined.
        </p>
        <div className="w-12 h-px bg-[#C8BAA8] mb-10" />
        <ProductGrid products={midRangeCurtains} cols={2} />
      </div>

      <div id="splurge-curtains" className="mb-16">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>Luxury</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>Splurge Picks — Luxury Linen Curtains</h2>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-2" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          For a more elevated result, these curtains offer exceptional fabric quality, considered drape, and the kind of quiet presence that makes a room feel complete.
        </p>
        <div className="w-12 h-px bg-[#C8BAA8] mb-10" />
        <ProductGrid products={splurgeCurtains} cols={2} />
      </div>

      <SectionDivider label="Complete the Look" />

      <div id="pair-with" className="mb-16">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>Style It</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>What to Pair With Linen Curtains</h2>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-2" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          Linen curtains work best when the rest of the room stays warm and layered. These are the pieces that complete the look.
        </p>
        <div className="w-12 h-px bg-[#C8BAA8] mb-10" />
        <ProductGrid products={pairWith} cols={2} />
      </div>

    </section>
  );
}
